import React, { useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import LinkIcon from '@mui/icons-material/Link';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CarouselComponent from '../Generic/CarouselComponent';
import { AppContext, CartAppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { ButtonGroup } from '@mui/material';
import TextField from '@mui/material/TextField';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { showToast } from '../../config_and_helpers/helpers';
import { getSupplierDetails } from '../../clients/supplier_client';
import { requestSupplierQuoteFromProduct } from '../../clients/quote_client';
import { catalogEmailSuccessMessage } from '../../utils/catalogQuoteEmailResponse';
import { logInfo } from '../../config_and_helpers/logger';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import {
  formatUnitQuantity,
  getProductCatalogNumber,
  getProductImageUrl,
  getProductImages,
  getProductInventoryId,
  getProductSupplierId,
} from '../../utils/productDisplay';

const LOG_CONTEXT = 'ShopModal';

const displayField = value =>
  value != null && String(value).trim() !== '' ? value : '—';

/**
 * Shop product detail modal (add to cart; supplier-catalog email quote request).
 *
 * @param {object} props
 * @param {object} props.product
 * @param {boolean} props.show
 * @param {function} props.setShow
 * @param {boolean} [props.isCatalogueView=false] - Supplier catalogue tab only
 * @param {boolean} [props.productLoading=false] - Detail fetch in progress
 */
const ShopModal = ({
  product,
  show = false,
  setShow,
  isCatalogueView = false,
  productLoading = false,
}) => {
  const { cart, setCart } = useContext(CartAppContext);
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [productAmount, setProductAmount] = useState('');
  const [supplier, setSupplier] = useState();
  const [isRequestingQuote, setIsRequestingQuote] = useState(false);
  const [quoteRequestErrors, setQuoteRequestErrors] = useState([]);
  const [emailSuccessMessage, setEmailSuccessMessage] = useState('');

  const catalogNumber = getProductCatalogNumber(product);
  const supplierId = getProductSupplierId(product);
  const canUseSupplierActions = Boolean(
    supplierId && (supplier?.id || product?.supplier_name)
  );

  useEffect(() => {
    if (product && supplierId) {
      getSupplierDetails(token, supplierId, setSupplier);
    }
  }, [product, supplierId, token]);

  const resolveSupplierForCart = () => {
    if (supplier?.id) {
      return { id: supplier.id, name: supplier.name };
    }
    if (supplierId && product?.supplier_name) {
      return { id: supplierId, name: product.supplier_name };
    }
    return null;
  };

  useEffect(() => {
    if (!show) {
      setQuoteRequestErrors([]);
      setEmailSuccessMessage('');
      setIsRequestingQuote(false);
    }
  }, [show]);

  const handleMinusClick = () => {
    if (productAmount <= 1) {
      setProductAmount('');
    } else {
      setProductAmount(prevState => prevState - 1);
    }
  };

  const handlePlusClick = () => {
    if (productAmount === 0) {
      setProductAmount(1);
    } else {
      setProductAmount(prevState => Number(prevState) + 1);
    }
  };

  const handleInputChange = value => {
    if (value < 1) {
      setProductAmount('');
    } else {
      setProductAmount(value);
    }
  };

  const handleAddToCart = () => {
    const supplierForCart = resolveSupplierForCart();
    if (!supplierForCart) {
      showToast('Supplier details are still loading. Try again.', 'warning', 'bottom-right', 2000);
      return;
    }

    const itemExists = cart.some(item => item.cat_num === catalogNumber);

    if (itemExists) {
      setCart(prevCart =>
        prevCart.map(item =>
          item.cat_num === catalogNumber
            ? {
                ...item,
                quantity: Number(item.quantity) + Number(productAmount),
              }
            : item
        )
      );
    } else {
      const newItem = {
        product: getProductInventoryId(product),
        cat_num: catalogNumber,
        name: product.name,
        image_url: getProductImageUrl(product, null),
        supplier: supplierForCart,
        quantity: Number(productAmount),
      };
      setCart(prevCart => [...prevCart, newItem]);
    }
    showToast('Product added to cart', 'success', 'bottom-right', 3000);
  };

  const handleRequestSupplierQuote = async () => {
    setIsRequestingQuote(true);
    setQuoteRequestErrors([]);
    setEmailSuccessMessage('');

    const payload = { quantity: Number(productAmount) };
    logInfo(LOG_CONTEXT, 'requestSupplierQuoteFromProduct submit', {
      productId: getProductInventoryId(product),
      quantity: payload.quantity,
    });

    const response = await requestSupplierQuoteFromProduct(
      token,
      getProductInventoryId(product),
      payload
    );
    setIsRequestingQuote(false);

    if (response?.success) {
      const msg = catalogEmailSuccessMessage(response.data);
      setEmailSuccessMessage(msg);
      showToast(msg, 'success', 'top-right', 5000);
      logInfo(LOG_CONTEXT, 'requestSupplierQuoteFromProduct success', {
        corporateDemandRef: response.data?.corporate_demand_ref,
        supplierCount: response.data?.suppliers?.length,
      });
    } else {
      setQuoteRequestErrors(
        response?.messages?.length
          ? response.messages
          : ['Request failed. Please try again.']
      );
    }
  };

  const handleClose = () => setShow(false);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80vw',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
  };

  const qtyValid = Number(productAmount) >= 1;
  const cartDisabled = !qtyValid || !canUseSupplierActions || productLoading;
  const quoteDisabled = cartDisabled || isRequestingQuote;

  return (
    <>
      <Modal open={show} onClose={handleClose} aria-labelledby="product-modal">
        <Box sx={modalStyle}>
          <Typography id="product-modal" variant="h3" component="h2">
            {product?.name ?? 'Product'}
          </Typography>

          {productLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading product details…
              </Typography>
            </Box>
          ) : null}

          <Divider sx={{ my: 2, borderColor: '#424242' }} />

          <Container>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Catalogue Number:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1">{catalogNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Stock:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1">
                  {displayField(product.stock)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Category:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1">
                  {displayField(product.category)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Supplier:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1">
                  {supplier ? (
                    <SupplierDetailModal supplierObj={supplier} />
                  ) : (
                    displayField(product.supplier_name)
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Unit:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1">
                  {displayField(product.unit)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Unit Quantity:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1">
                  {formatUnitQuantity(product.unit_quantity) || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Storage:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1">
                  {displayField(product.storage)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" className="fw-bold">
                  Website Profile:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                {product.url ? (
                  <a href={product.url}>
                    <LinkIcon sx={{ fontSize: '36px' }} />
                  </a>
                ) : (
                  <Typography variant="subtitle1">—</Typography>
                )}
              </Grid>
            </Grid>
          </Container>

          <Divider sx={{ my: 2, borderColor: '#424242' }} />

          <CarouselComponent
            images={getProductImages(product)}
            imageIdKind="productImageId"
          />

          <Divider sx={{ my: 2, borderColor: '#424242' }} />

          {quoteRequestErrors.length > 0 ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {quoteRequestErrors.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </Alert>
          ) : null}
          {emailSuccessMessage ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {emailSuccessMessage}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <ButtonGroup
              disableElevation
              variant="contained"
              aria-label="Product quantity"
            >
              <Button onClick={handleMinusClick}>-</Button>
              <TextField
                value={productAmount}
                onChange={e => handleInputChange(e.target.value)}
                id="outlined-number"
                label="Amount"
                InputLabelProps={{ shrink: true }}
                onKeyPress={e => {
                  if (e.key.match(/[^0-9]/)) {
                    e.preventDefault();
                  }
                }}
              />
              <Button onClick={handlePlusClick}>+</Button>
            </ButtonGroup>

            {!isViewer ? (
            <Button
              variant="outlined"
              onClick={handleAddToCart}
              disabled={cartDisabled}
            >
              <AddShoppingCartIcon />
            </Button>
            ) : null}

            {!isViewer && isCatalogueView ? (
              <Button
                variant="contained"
                color="secondary"
                startIcon={
                  isRequestingQuote ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <RequestQuoteIcon />
                  )
                }
                onClick={handleRequestSupplierQuote}
                disabled={quoteDisabled}
              >
                Request quote from supplier
              </Button>
            ) : null}

            <Button variant="outlined" onClick={handleClose} sx={{ ml: 'auto' }}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ShopModal;
